<script lang="ts">
    /**
     * PinHeroSection - Pin 제품 페이지 히어로 (/pin)
     *
     * CompanyHeroSection 패턴(중앙 정렬 · eyebrow + title + subtitle + CTA)을 따른다.
     * GridverseHeroSection 의 "좌 텍스트 + 우 폰 목업" 을 쓰지 않는 이유는 **Pin 이 앱이 아니라
     * 웹 콘텐츠 서비스**여서다 — 보여줄 앱 화면이 없다. 없는 목업을 그리면 그게 거짓말이다.
     * accent = #2E6F9E(길·표지판 계열 블루). JadEye(jade)·Gridverse(gold)와 구분한다.
     *
     * 🚨 카피는 "지금 실제로 있는 것"만 말한다. 클릭 후 없는 걸 찾으면 이탈로 돌아온다.
     *    발행된 축은 현재 4개다(휴게소 · 노포 · 타임터널 · 등산코스). 축 개수를 문장에 박지 말 것 —
     *    축이 늘 때마다 여기가 stale 해진다. 목록이 필요하면 서버가 주는 pin.axes 를 순회한다.
     */
    import { getPin } from '../../../../lib/pin';

    const pin = getPin();

    let isVisible = $state(false);

    $effect(() => {
        const t = setTimeout(() => {
            isVisible = true;
        }, 100);
        return () => clearTimeout(t);
    });

    /** 히어로 CTA — 서버가 준 축 목록 그대로다. 상위 2개만 버튼으로 내고 나머지는 아래 카드가 받는다. */
    const ctaAxes = (pin?.axes ?? []).slice(0, 2);

    function scrollToConcept(e: MouseEvent) {
        e.preventDefault();
        // 서비스 메뉴("무엇을 찾으세요?")가 이 질문의 답이다 — 모든 하위 서비스가 거기서 갈라진다
        document.querySelector('#pin-services')?.scrollIntoView({ behavior: 'smooth' });
    }
</script>

<section class="pin-hero">
    <div class="hero-inner" class:visible={isVisible}>
        <span class="hero-eyebrow">서비스 중</span>
        <h1 class="hero-title">
            길 위에서 찾는<br />
            진짜 정보
        </h1>
        <p class="hero-subtitle">
            Pin은 길 위에서 필요한 정보를 모아 보여주는 서비스입니다.<br />
            <strong>고속도로 휴게소</strong>의 메뉴·편의시설부터 <strong>오래된 식당</strong>과 <strong>등산코스</strong>까지.
        </p>

        <div class="hero-actions">
            <!-- 소개 페이지의 목적은 이 한 번의 클릭이다 — 실제 콘텐츠(SSR)로 바로 보낸다.
                 목록은 서버(pin.axes)가 정한다: 축이 늘어도 여기를 고칠 일이 없다. -->
            {#each ctaAxes as axis (axis.path)}
                <a href={axis.path} class="hero-cta-primary">
                    {axis.label}
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
                    </svg>
                </a>
            {/each}
            <a href="#pin-services" class="hero-cta-secondary" onclick={scrollToConcept}>
                <span>무엇을 볼 수 있나요?</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M12 5v14" /><path d="m19 12-7 7-7-7" />
                </svg>
            </a>
        </div>

        <p class="hero-note">
            <!-- 등산은 정기 적재 스케줄이 없다 — "주기적 갱신" 은 휴게소·식당에만 쓴다(애드센스 재심사 계획 §2). -->
            휴게소·식당 정보는 한국도로공사·행정안전부 공공데이터로 주기적으로 갱신하고, 등산코스는 국립공원공단·한국등산트레킹지원센터 원본 자료를 기준으로 해요.<br />
            메뉴와 가격은 현장 사정에 따라 다를 수 있어요.
        </p>
    </div>
</section>

<style>
    .pin-hero {
        min-height: 62vh;
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding: 7rem 1.5rem 3rem;
        /* jade 톤 팔레트 안에서 Pin 만 blue 쪽으로 살짝 기운다 — 회사 홈과 이질감 없이 구분된다 */
        background: linear-gradient(135deg, #eff5f9 0%, #e8f0f6 30%, #fafbfc 100%);
        position: relative;
        overflow: hidden;
    }

    .pin-hero::before {
        content: '';
        position: absolute;
        top: -50%;
        right: -30%;
        width: 80%;
        height: 80%;
        background: radial-gradient(circle, rgba(46, 111, 158, 0.07) 0%, transparent 70%);
        pointer-events: none;
    }

    .hero-inner {
        max-width: 48rem;
        margin: 0 auto;
        width: 100%;
        text-align: center;
        opacity: 0;
        transform: translateY(20px);
        transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
    }

    .hero-inner.visible {
        opacity: 1;
        transform: translateY(0);
    }

    .hero-eyebrow {
        display: inline-block;
        font-size: 0.9rem;
        font-weight: 600;
        letter-spacing: 0.04em;
        color: #2e6f9e;
        background: rgba(46, 111, 158, 0.1);
        padding: 0.4rem 1rem;
        border-radius: 999px;
        margin-bottom: 1.5rem;
    }

    .hero-title {
        font-size: clamp(2.2rem, 5vw, 3.5rem);
        font-weight: 700;
        line-height: 1.25;
        letter-spacing: -0.03em;
        color: var(--landing-text, #1a1a1a);
        margin: 0 0 1.5rem;
    }

    .hero-subtitle {
        font-size: 1.1rem;
        color: var(--landing-text-sub, #666666);
        line-height: 1.8;
        margin: 0 0 2.25rem;
    }

    .hero-subtitle strong {
        color: #2e6f9e;
        font-weight: 600;
    }

    .hero-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 0.75rem;
        justify-content: center;
        align-items: center;
    }

    .hero-cta-primary {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.9rem 1.8rem;
        background: #2e6f9e;
        color: #ffffff;
        font-size: 1rem;
        font-weight: 600;
        text-decoration: none;
        border-radius: 999px;
        box-shadow: 0 8px 24px rgba(46, 111, 158, 0.25);
        transition:
            transform 0.2s ease,
            box-shadow 0.2s ease;
    }

    .hero-cta-primary:hover {
        transform: translateY(-2px);
        box-shadow: 0 12px 32px rgba(46, 111, 158, 0.32);
    }

    .hero-cta-secondary {
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
        padding: 0.9rem 1.4rem;
        color: var(--landing-text-sub, #666666);
        font-size: 0.95rem;
        font-weight: 500;
        text-decoration: none;
        border-radius: 999px;
        transition:
            color 0.2s ease,
            background 0.2s ease;
    }

    .hero-cta-secondary:hover {
        color: #2e6f9e;
        background: rgba(46, 111, 158, 0.07);
    }

    .hero-note {
        margin: 2rem 0 0;
        font-size: 0.82rem;
        line-height: 1.7;
        color: var(--landing-text-sub, #666666);
        opacity: 0.85;
    }

    @media (min-width: 768px) {
        .pin-hero {
            padding: 7rem 2.5rem 3rem;
        }
    }

    @media (min-width: 1024px) {
        .pin-hero {
            padding: 7rem 3rem 3rem;
        }
    }
</style>
